import { FACILITY_TYPE } from "../../../constants/intxConstants";
import { DEMAND_PROFILES_PARAM_KEYS } from "../../../constants/storageConstants";
import { DAY_OF_WEEK, HOURS, MONTH } from "../../../constants/timeConstants";
import { sumValues } from "../../../util/mathHelpers.ts";

// these values stored in /public so they can be not minified in the build since PENN DOT wants to be able to edit the values
const { HOURLY_PROFILES_WEEKDAY, HOURLY_PROFILES_WEEKEND } =
  window.PENN_DOT_CONSTANTS.DEMAND_PROFILES;

const DAY_TO_LABEL = {
  [DAY_OF_WEEK.MONDAY]: "Monday",
  [DAY_OF_WEEK.TUESDAY]: "Tuesday",
  [DAY_OF_WEEK.WEDNESDAY]: "Wednesday",
  [DAY_OF_WEEK.THURSDAY]: "Thursday",
  [DAY_OF_WEEK.FRIDAY]: "Friday",
  [DAY_OF_WEEK.SATURDAY]: "Saturday",
  [DAY_OF_WEEK.SUNDAY]: "Sunday",
};

const MONTH_TO_LABEL = {
  [MONTH.JANUARY]: "January",
  [MONTH.FEBRUARY]: "February",
  [MONTH.MARCH]: "March",
  [MONTH.APRIL]: "April",
  [MONTH.MAY]: "May",
  [MONTH.JUNE]: "June",
  [MONTH.JULY]: "July",
  [MONTH.AUGUST]: "August",
  [MONTH.SEPTEMBER]: "September",
  [MONTH.OCTOBER]: "October",
  [MONTH.NOVEMBER]: "November",
  [MONTH.DECEMBER]: "December",
};

const HOUR_LABELS = {
  [HOURS["12AM"]]: "12:00 AM",
  [HOURS["1AM"]]: "1:00 AM",
  [HOURS["2AM"]]: "2:00 AM",
  [HOURS["3AM"]]: "3:00 AM",
  [HOURS["4AM"]]: "4:00 AM",
  [HOURS["5AM"]]: "5:00 AM",
  [HOURS["6AM"]]: "6:00 AM",
  [HOURS["7AM"]]: "7:00 AM",
  [HOURS["8AM"]]: "8:00 AM",
  [HOURS["9AM"]]: "9:00 AM",
  [HOURS["10AM"]]: "10:00 AM",
  [HOURS["11AM"]]: "11:00 AM",
  [HOURS["12PM"]]: "12:00 PM",
  [HOURS["1PM"]]: "1:00 PM",
  [HOURS["2PM"]]: "2:00 PM",
  [HOURS["3PM"]]: "3:00 PM",
  [HOURS["4PM"]]: "4:00 PM",
  [HOURS["5PM"]]: "5:00 PM",
  [HOURS["6PM"]]: "6:00 PM",
  [HOURS["7PM"]]: "7:00 PM",
  [HOURS["8PM"]]: "8:00 PM",
  [HOURS["9PM"]]: "9:00 PM",
  [HOURS["10PM"]]: "10:00 PM",
  [HOURS["11PM"]]: "11:00 PM",
};

export const TIME_PERIOD_TO_LABEL = {
  ...DAY_TO_LABEL,
  ...MONTH_TO_LABEL,
  ...HOUR_LABELS,
};

export const PARAM_KEY_TO_PROFILE_TYPE = {
  [DEMAND_PROFILES_PARAM_KEYS.DAILY]: "DAILY_PROFILES",
  [DEMAND_PROFILES_PARAM_KEYS.MONTHLY]: "MONTHLY_PROFILES",
  [DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKDAY]: "HOURLY_PROFILES_WEEKDAY",
  [DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKEND]: "HOURLY_PROFILES_WEEKEND",
};

const getHourlyProfilesByFacilityType = (facilityType) => {
  const indexByFacilityType = (accumulator, { timePeriod, ...rest }) => ({
    ...accumulator,
    [timePeriod]: rest[facilityType] * 100, // multiply by 100 to match the user input which will be 2.5% or 17.2% as opposed to 0.025 or 0.172
  });
  const weekday = HOURLY_PROFILES_WEEKDAY.reduce(indexByFacilityType, {});
  const weekend = HOURLY_PROFILES_WEEKEND.reduce(indexByFacilityType, {});
  return {
    [DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKDAY]: weekday,
    [DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKEND]: weekend,
  };
};

const HOURLY_PROFILES_BY_FACILITY_TYPE = [
  FACILITY_TYPE.RURAL_PRINCIPAL_ARTERIAL,
  FACILITY_TYPE.RURAL_MINOR_ARTERIAL,
  FACILITY_TYPE.RURAL_MAJOR_COLLECTOR,
  FACILITY_TYPE.RURAL_MINOR_COLLECTOR,
  FACILITY_TYPE.URBAN_PRINCIPAL_ARTERIAL,
  FACILITY_TYPE.URBAN_MINOR_ARTERIAL,
  FACILITY_TYPE.URBAN_MAJOR_COLLECTOR,
].reduce(
  (accumulator, facilityType) => ({
    ...accumulator,
    [facilityType]: getHourlyProfilesByFacilityType(facilityType),
  }),
  {}
);

export const PROFILES_WITH_SPECIFIC_TOTALS = [
  DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKDAY,
  DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKEND,
];

/**
 * @param {string} faciliytType the facility type selected in the global inputs step
 * @param {string} formData the current values of the form fields
 * @param {string} paramKey the type of form data that we want props for (daily, monthly, hourlyWeekday, hourlyWeekend)
 * @returns the sum of the hourly profiles by combining the defaults and the most recent user-input values. Sum should be 100%
 */
export const getHourlyProfileTotal = ({ facilityType, formData, paramKey }) => {
  // object of values that the user has updated
  const touchedFormValues = Object.entries(formData?.[paramKey] || {})
    .filter(([field, value]) => !!value || value === 0)
    .reduce(
      (accumulator, [field, value]) => ({
        ...accumulator,
        [field]: value,
      }),
      {}
    );
  return sumValues({
    ...HOURLY_PROFILES_BY_FACILITY_TYPE[facilityType][paramKey], // default values
    ...touchedFormValues, // user input values
  });
};
